import type { ConversationType, ItemRow } from 'owa-graph-schema';
import { store } from '../store/store';
import type { MailListRowDataType } from 'owa-mail-list-store';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { logUsage } from 'owa-analytics';
import {
    filterItemRowsForCatchUp,
    filterConversationRowsForCatchUp,
} from 'owa-copilot-headlines-option/lib/utils/copilotCatchUpFilterUtils';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Filters email rows for item view.
 * Returns only those rows whose ItemId matches an ItemId returned by the experimentAPI
 */
export const filterEmailsWithScheduleTasksForItemView = (
    rows: ItemRow[],
    mailboxInfo: MailboxInfo
): MailListRowDataType[] => {
    const itemsIdsWithSchedulingTasks = store.itemsIdsWithSchedulingTasks;
    if (itemsIdsWithSchedulingTasks.length === 0) {
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logUsage('CopilotCatchUpScheduleFilterRowCount', undefined, { count: 0, view: 'item' });
        return [];
    }

    let filteredRows = rows.filter(row => {
        if (!row.ItemId?.Id) {
            return false;
        }
        return itemsIdsWithSchedulingTasks.some(id => convertRestIdToEwsId(id) === row.ItemId.Id);
    }) as MailListRowDataType[];

    // Filter according to settings
    filteredRows = filterItemRowsForCatchUp(
        filteredRows as ItemRow[],
        'catchUpNeedsScheduleFilter',
        mailboxInfo
    );

    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
    logUsage('CopilotCatchUpScheduleFilterRowCount', undefined, {
        count: filteredRows.length,
        view: 'item',
    });
    return filteredRows;
};

/**
 * Filters conversation threads for conversation view.
 * Returns only those conversations where at least one of multiple ItemIds matches an ID returned by the experimentAPI.
 */
export const filterEmailsWithScheduleTasksForConversationView = (
    rows: ConversationType[],
    mailboxInfo: MailboxInfo
): MailListRowDataType[] => {
    const itemsIdsWithSchedulingTasks = store.itemsIdsWithSchedulingTasks;
    if (itemsIdsWithSchedulingTasks.length === 0) {
        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logUsage('CopilotCatchUpScheduleFilterRowCount', undefined, {
            count: 0,
            view: 'conversation',
        });
        return [];
    }

    let filteredRows = rows.filter(conversation => {
        const threadMessageIds = conversation.ItemIds as {
            Id: string;
        }[];
        if (!threadMessageIds?.length) {
            return false;
        }

        return itemsIdsWithSchedulingTasks.some(schedulingTaskId =>
            threadMessageIds.some(
                messageId => convertRestIdToEwsId(schedulingTaskId) === messageId.Id
            )
        );
    }) as MailListRowDataType[];

    filteredRows = filterConversationRowsForCatchUp(
        filteredRows as ConversationType[],
        'catchUpNeedsScheduleFilter',
        mailboxInfo
    );

    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
    logUsage('CopilotCatchUpScheduleFilterRowCount', undefined, {
        count: filteredRows.length,
        view: 'conversation',
    });
    return filteredRows;
};
